import { useState, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { Button, Typography, Grid, Stack, Box, Link } from '@mui/material_modern'
import { DateRange, Lock, LockOpen } from '@mui/icons-material_modern'
import { useGetLockedEntitiesLazyQuery } from 'services/lockAccounting'
import DatePicker from 'components/generic/form/DatePicker'
import LockAccountingModal from 'components/layouts/settings/components/LockAccountingModal'
import { getISOLocaleEndOfDay } from 'utils/data-manipulation/valueFormatter'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'

const helpArticleLink = (
  <Link target="_blank" href="https://intercom.help/greenspark-software/en/articles/9917622-locking-accounting-periods">
    help article
  </Link>
)

const SettingsLockAccounting: React.FC = () => {
  const { control, watch, setValue } = useFormContext()
  const isLockedForAccounting = watch('overviewSettings.overview.lockAccountingPeriod.isLockedForAccounting')
  const lockAccountingDate = watch('overviewSettings.overview.lockAccountingPeriod.lockAccountingDate')
  const formattedLockAccountingDate = getISOLocaleEndOfDay((lockAccountingDate || new Date()).valueOf())
  const [showModal, setShowModal] = useState(false)

  const { enableLockAccountingV2 } = useAppSelector(state => state.featureFlags?.variables || {})

  const [getLockedEntities, { data: lockedEntities, isFetching: isFetchingLockedEntities }] =
    useGetLockedEntitiesLazyQuery({})

  useEffect(() => {
    if (!lockedEntities || isFetchingLockedEntities) return
    if (lockedEntities.Items?.length) {
      setShowModal(true)
    } else {
      setValue('overviewSettings.overview.lockAccountingPeriod.isLockedForAccounting', true, {
        shouldDirty: true,
      })
    }
  }, [lockedEntities, isFetchingLockedEntities])

  return (
    <>
      {showModal && (
        <LockAccountingModal
          open={showModal}
          onClose={() => setShowModal(false)}
          onLock={() => {
            setValue('overviewSettings.overview.lockAccountingPeriod.isLockedForAccounting', !isLockedForAccounting, {
              shouldDirty: true,
            })
          }}
          formattedLockAccountingDate={formattedLockAccountingDate}
        />
      )}
      <Typography variant="h6" color="textPrimary" data-testid="title">
        Lock Accounting Period
      </Typography>
      {enableLockAccountingV2 ? (
        <Typography variant="body2" color="textSecondary" data-testid="subtitle">
          Any changes made to price complete & approved inbound tickets, bills, and shipped outbound tickets with
          effective dates in the locked accounting period will realize inventory impact on the date of action, rather
          than the effective date. New tickets, bills, and invoices cannot be dated prior to the locked accounting
          period. For more detailed information, please see the {helpArticleLink}.
        </Typography>
      ) : (
        <Typography variant="body2" color="textSecondary" data-testid="subtitle">
          Any tickets, bills, and invoices dated prior to the locked accounting period will not be editable. New
          tickets, bills, and invoices cannot be dated prior to the set accounting period.
        </Typography>
      )}
      <Grid mt={2}>
        <Stack direction="row" spacing={1}>
          <Box width="200px">
            <DatePicker
              name="overviewSettings.overview.lockAccountingPeriod.lockAccountingDate"
              label="Locked Date"
              data-testid="lock-accounting-date"
              variant="outlined"
              size="small"
              control={control}
              disabled={isLockedForAccounting}
              datePickerProps={{
                disabled: isLockedForAccounting,
                components: {
                  OpenPickerIcon: DateRange,
                },
              }}
              defaultDate={new Date()}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Button
            disabled={isFetchingLockedEntities || showModal}
            variant="outlined"
            startIcon={isLockedForAccounting ? <LockOpen /> : <Lock />}
            data-testid="lock-accounting-button"
            onClick={() => {
              if (!isLockedForAccounting) {
                getLockedEntities({
                  page: 1,
                  pageSize: 10,
                  filter: JSON.stringify({
                    lockAccountingDate: formattedLockAccountingDate,
                  }),
                })
              } else {
                setValue('overviewSettings.overview.lockAccountingPeriod.isLockedForAccounting', false, {
                  shouldDirty: true,
                })
              }
            }}
          >
            {isLockedForAccounting ? 'Unlock' : 'Set New Locked Period'}
          </Button>
        </Stack>
      </Grid>
    </>
  )
}

export default SettingsLockAccounting
